import { BdLabelColorDirective } from '@bindoc-projects/labels-lib/ui';
import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  LOCALE_ID,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import {
  BD_SHARE_PERMISSION_DISPLAY_NAME,
  BdResource,
  BdSharePermissionHelper,
  SHAREABLE_TYPE_DISPLAY_NAME,
  ShareableLabel,
} from '@bindoc-projects/resource-domain-lib';
import { BdHelpLink, BdHelpService } from '@bindoc-projects/help-lib/data';
import { BdDataTestDirective } from '@bindoc-projects/util-lib/ui';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { ButtonDirective } from 'primeng/button';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Menu } from 'primeng/menu';
import { Skeleton } from 'primeng/skeleton';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { BdShareableSelectService } from '../select/global-search/shareable-select.service';
import { BD_ARCHIVE_SHAREABLE_OPENER } from '../archive-shareable/archive-shareable.token';
import { BD_EDIT_LABELS_OPENER } from '../edit-labels/edit-labels.token';
import { BD_MOVE_OPENER } from '../move/move.token';
import { BD_PERMISSIONS_OPENER } from '../permissions-access/permissions-access.token';
import { BD_SHARE_OPENER } from '../share/share.token';
import { BdWorkspaceLinkService } from '../workspace-link/workspace-link.service';
import {
  BD_DANGER_MENU_ITEM_CSS,
  BD_DANGER_MENU_ITEM_STYLE,
} from './danger-menu-item';
import {
  BD_RESOURCES_INFO_MENU_DEFAULT_CONFIG,
  BdResourcesInfoMenuConfig,
} from './info-menu';
import { BdShareablesService } from '@bindoc-projects/bindoc-permissions-lib/data';

@Component({
  selector: 'bd-resources-info-menu',
  template: `
    <button
      pButton
      aria-label="Aktionen"
      title="Aktionen"
      severity="secondary"
      text
      rounded
      [data-test]="
        'resource-info-' + $safeNavigationMigration(resource()?.resourceName)
      "
      (click)="onMenuShow(); menu.toggle($event)"
      iconOnly
    >
      <i class="pi pi-ellipsis-v" aria-hidden="true"></i>
    </button>
    <p-confirm-dialog></p-confirm-dialog>
    <p-menu #menu [model]="menuItems()" [popup]="true">
      <ng-template #start>
        @if (resource()) {
          <div class="px-4 py-3 border-b border-surface flex flex-col gap-3">
            <div class="flex gap-2">
              <i class="pi pi-eye text-muted-color mt-0.5"></i>
              <div class="flex flex-col">
                <span class="text-sm">Berechtigung</span>
                <span class="font-semibold">{{ permissionDisplayText() }}</span>
              </div>
            </div>
            @if (resource().createdDate) {
              <div class="flex gap-2">
                <i class="pi pi-calendar-clock text-muted-color mt-0.5"></i>
                <div class="flex flex-col">
                  <span class="text-sm">Erstellt</span>
                  <span class="font-semibold">{{
                    resource().createdDate | date: 'medium'
                  }}</span>
                </div>
              </div>
            }
            <div class="flex gap-2">
              <i class="pi pi-calendar-clock text-muted-color mt-0.5"></i>
              <div class="flex flex-col">
                <span class="text-sm">Geändert</span>
                <span class="font-semibold">{{
                  resource().modifiedDate | date: 'medium'
                }}</span>
              </div>
            </div>
            <div class="flex gap-2">
              <i class="pi pi-tags text-muted-color mt-0.5"></i>
              <div class="flex flex-col gap-1">
                <span class="text-sm">Labels</span>
                @if (labelsLoading()) {
                  <p-skeleton width="5rem" height="1.25rem" />
                } @else if (currentLabels().length > 0) {
                  <span class="inline-flex flex-wrap gap-1">
                    @for (label of currentLabels(); track label.text) {
                      <span
                        class="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs"
                        [bdLabelColor]="label.color"
                      >
                        <i class="pi pi-tag text-xs"></i>
                        {{ label.text }}
                      </span>
                    }
                  </span>
                } @else {
                  <span class="text-sm text-muted-color">Keine Labels</span>
                }
              </div>
            </div>
          </div>
        }
      </ng-template>
    </p-menu>
  `,
  styles: [BD_DANGER_MENU_ITEM_CSS],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    BdLabelColorDirective,
    BdDataTestDirective,
    Menu,
    DatePipe,
    ConfirmDialog,
    ButtonDirective,
    Skeleton,
  ],
  providers: [ConfirmationService],
})
export class BdResourcesInfoMenuComponent {
  readonly resource = input<BdResource>();
  readonly helpKey = input<string>();
  readonly config = input<BdResourcesInfoMenuConfig>(
    BD_RESOURCES_INFO_MENU_DEFAULT_CONFIG,
  );
  /**
   * Extra items shown in their own labelled section below the resource's own
   * actions — the tool-connect "Öffnen mit …" hand-offs of a detail page.
   * Kept a plain `MenuItem[]` so this lib does not depend on the tool-connect
   * lib; hosts wire the two together (same contract as
   * `bd-chart-export-menu`'s `additionalItems`).
   *
   * A detail page header must not grow a second ellipsis trigger next to this
   * one, so the hand-offs live in this menu rather than in their own.
   */
  readonly additionalItems = input<MenuItem[]>([]);
  /** Heading of the {@link additionalItems} section. */
  readonly additionalItemsLabel = input('Öffnen mit …');
  /**
   * Heading the resource's own actions get once {@link additionalItems} splits
   * the menu into sections. Unused while the menu is flat.
   */
  readonly ownItemsLabel = input('Aktionen');
  /**
   * The resource's labels, when the host already holds them (e.g. the file
   * detail page renders them on its own card). While set, the menu never
   * fetches `/shareables/{type}/{id}/labels/` itself. Bind it two-way
   * (`[(labels)]`) so an edit made via "Labels bearbeiten" flows back to the
   * host; otherwise the menu would keep showing the host's stale list.
   */
  readonly labels = input<ShareableLabel[] | undefined>(undefined);

  readonly editResource = output<void>();
  readonly deleteResource = output<void>();
  readonly shared = output<void>();
  /** Emits the target folder id once the resource was moved to another folder. */
  readonly moved = output<string>();
  /** Emits the saved label list after "Labels bearbeiten" (see {@link labels}). */
  readonly labelsChange = output<ShareableLabel[]>();

  public readonly permissionDisplayText = signal('');
  public readonly labelsLoading = signal(false);
  /**
   * Labels shown in the popup header. The host's {@link labels} win; without
   * them the menu loads its own on first open (BDA-5554) — the labels are only
   * visible inside the popup, so fetching them on every detail-page load was a
   * request most visits never looked at.
   */
  public readonly currentLabels = computed<ShareableLabel[]>(
    () => this.labels() ?? this._fetchedLabels() ?? [],
  );

  /** `undefined` = not loaded yet for the current resource. */
  private readonly _fetchedLabels = signal<ShareableLabel[] | undefined>(
    undefined,
  );
  private _labelsRequest: Subscription | undefined;

  private readonly _ownMenuItems = signal<MenuItem[]>([]);

  /**
   * The menu model. Flat while nothing else contributes items — `p-menu`
   * switches to grouped rendering as soon as **any** root entry carries
   * `items` and then renders every other root entry as a section heading, so
   * the two shapes cannot be mixed.
   */
  public readonly menuItems = computed<MenuItem[]>(() => {
    const own = this._ownMenuItems();
    const additional = this.additionalItems();
    if (additional.length === 0) {
      return own;
    }

    const sections: MenuItem[] = [];
    if (own.length > 0) {
      sections.push({ label: this.ownItemsLabel(), items: own });
    }
    sections.push({ label: this.additionalItemsLabel(), items: additional });
    return sections;
  });

  private _showEditButton = false;
  private _showDeleteButton = false;
  private _showEditLabelsButton = false;
  private _showShareButton = false;
  private _showPermissionsButton = false;
  private _helpLink: BdHelpLink | undefined;

  private readonly _locale = inject(LOCALE_ID);
  private readonly _confirmService = inject(ConfirmationService);
  private readonly _helpService = inject(BdHelpService);
  private readonly _permissionsService = inject(BdShareablesService);
  private readonly _selectService = inject(BdShareableSelectService);
  private readonly _workspaceLinks = inject(BdWorkspaceLinkService);
  private readonly _editLabelsOpener = inject(BD_EDIT_LABELS_OPENER, {
    optional: true,
  });
  private readonly _shareOpener = inject(BD_SHARE_OPENER, { optional: true });
  private readonly _moveOpener = inject(BD_MOVE_OPENER, { optional: true });
  private readonly _permissionsOpener = inject(BD_PERMISSIONS_OPENER, {
    optional: true,
  });
  private readonly _archiveOpener = inject(BD_ARCHIVE_SHAREABLE_OPENER, {
    optional: true,
  });

  constructor() {
    effect(() => {
      const helpKey = this.helpKey();
      this._helpService.findByKey(helpKey).then((helpLink) => {
        this._helpLink = helpLink;
        this._buildMenuItems();
      });
    });

    effect(() => {
      const resource = this.resource();
      // A new resource invalidates labels loaded for the previous one.
      this._resetFetchedLabels();
      if (resource) {
        this._loadPermissions();
      }
    });
  }

  /**
   * Called from the trigger, not from `p-menu`'s `(onShow)`: PrimeNG emits that
   * from an overlay motion hook, which the click path does not depend on.
   */
  public onMenuShow(): void {
    this._loadLabelsIfNeeded();
  }

  private _loadPermissions(): void {
    const resource = this.resource();
    const config = this.config();
    this._permissionsService
      .myPermission(resource.resourceName, resource.id)
      .subscribe((it) => {
        this.permissionDisplayText.set(
          BD_SHARE_PERMISSION_DISPLAY_NAME[this._locale][it.highestPermission],
        );
        this._showDeleteButton =
          config.showDeleteButton &&
          BdSharePermissionHelper.includes(it.highestPermission, 'DELETE');
        this._showEditButton =
          config.showEditButton &&
          BdSharePermissionHelper.includes(it.highestPermission, 'UPDATE');
        this._showEditLabelsButton =
          !!this._editLabelsOpener &&
          BdSharePermissionHelper.includes(it.highestPermission, 'UPDATE');
        // Sharing requires DELETE: the grant dialog's first step calls the
        // share-preview endpoint, which the backend gates on DELETE. Gating the
        // entry on the weaker UPDATE let UPDATE-only users open a dialog that
        // immediately 403s into a dead end (BDA-5507).
        this._showShareButton =
          config.showShareButton &&
          !!this._shareOpener &&
          BdSharePermissionHelper.includes(it.highestPermission, 'DELETE');
        this._showPermissionsButton =
          config.showPermissionsButton &&
          !!this._permissionsOpener &&
          BdSharePermissionHelper.includes(it.highestPermission, 'UPDATE');

        this._buildMenuItems();
      });
  }

  private _loadLabelsIfNeeded(): void {
    const resource = this.resource();
    if (
      this.labels() !== undefined ||
      this._fetchedLabels() !== undefined ||
      this.labelsLoading() ||
      !resource?.id ||
      !resource?.resourceName
    ) {
      return;
    }
    this.labelsLoading.set(true);
    this._labelsRequest = this._permissionsService
      .labels(resource.resourceName, resource.id)
      .pipe(take(1))
      .subscribe({
        next: (labels) => this._finishLabelsRequest(labels ?? []),
        error: () => this._finishLabelsRequest([]),
      });
  }

  private _finishLabelsRequest(labels: ShareableLabel[]): void {
    this._fetchedLabels.set(labels);
    this.labelsLoading.set(false);
    this._labelsRequest = undefined;
  }

  private _resetFetchedLabels(): void {
    this._labelsRequest?.unsubscribe();
    this._labelsRequest = undefined;
    this._fetchedLabels.set(undefined);
    this.labelsLoading.set(false);
  }

  /**
   * Builds the menu in the canonical order shared with the shareables-tab /
   * recent-tab / grants-tab providers:
   *
   *   1. Hilfeseiten
   *   2. In neuem Tab öffnen
   *   3. Im Ordner öffnen
   *   4. Bearbeiten
   *   5. Labels bearbeiten
   *   6. Verschieben
   *   7. Freigeben
   *   8. Zugriff
   *   9. Löschen (danger)
   */
  private _buildMenuItems(): void {
    const resource = this.resource();
    const items: MenuItem[] = [];

    if (this._helpLink) {
      items.push({
        label: `Hilfeseiten ${this._helpLink.displayName}`,
        icon: 'pi pi-question-circle',
        url: this._helpLink.link,
        target: '_blank',
      });
    }

    const resourceId = resource?.id;
    const resourceType = resource?.resourceName;
    if (resourceId && resourceType) {
      items.push({
        label: 'In neuem Tab öffnen',
        icon: 'pi pi-window-maximize',
        command: () =>
          this._selectService.openInNewTab(resourceId, resourceType),
      });
    }

    // Hidden where the app has no workspace and no origin to link to (see
    // BD_WORKSPACE_LOCATION); an app without its own workspace opens the
    // meta-app's in a new tab instead of routing to a not-found page.
    if (resourceId && resourceType && this._workspaceLinks.available) {
      items.push({
        label: 'Im Ordner öffnen',
        icon: 'pi pi-folder-open',
        command: () => this._openInWorkspaceFolder(resourceId),
      });
    }

    if (this._showEditButton) {
      items.push({
        label: 'Bearbeiten',
        icon: 'pi pi-pencil',
        command: () => this.editResource.emit(),
      });
    }

    if (this._showEditLabelsButton) {
      items.push({
        label: 'Labels bearbeiten',
        icon: 'pi pi-tags',
        command: () => this._openEditLabelsDialog(),
      });
    }

    // Not permission-gated — same as the "Verschieben" entry of the workspace's
    // row menus, which open the very same dialog. Hidden in apps that don't
    // provide it (BD_MOVE_OPENER).
    if (resourceId && resourceType && this._moveOpener) {
      items.push({
        label: 'Verschieben',
        icon: 'pi pi-arrow-right',
        command: () => this._openMoveDialog(),
      });
    }

    if (this._showShareButton) {
      items.push({
        label: 'Freigeben',
        icon: 'pi pi-share-alt',
        command: () => this._openShareDialog(),
      });
    }

    if (this._showPermissionsButton) {
      items.push({
        label: 'Zugriff',
        icon: 'pi pi-key',
        command: () => this._openPermissionsDialog(),
      });
    }

    if (this._showDeleteButton) {
      items.push({
        label: 'Löschen',
        icon: 'pi pi-trash',
        ...BD_DANGER_MENU_ITEM_STYLE,
        command: () => this._confirmDelete(),
      });
    }

    this._ownMenuItems.set(items);
  }

  /**
   * Deep-links to the workspace and lets `BdShareablesOverviewComponent`
   * resolve the parent folder from the shareable id. The receiver also
   * highlights the matching row, so the user can see exactly where the
   * resource lives without us looking it up here.
   */
  private _openInWorkspaceFolder(shareableId: string): void {
    this._workspaceLinks.open({ tab: 'shareables', shareableId });
  }

  public onEditLabelsLink(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this._openEditLabelsDialog();
  }

  private _openShareDialog(): void {
    const resource = this.resource();
    const opener = this._shareOpener;
    if (!resource?.id || !resource?.resourceName || !opener) {
      return;
    }
    opener({
      shareableType: resource.resourceName,
      shareableId: resource.id,
      shareableName: resource.name,
      shareableTypeLabel: SHAREABLE_TYPE_DISPLAY_NAME[resource.resourceName],
      modifiedDate: resource.modifiedDate?.toISOString(),
    })
      .pipe(take(1))
      .subscribe((result) => {
        if (result?.granted) {
          this.shared.emit();
        }
      });
  }

  private _openMoveDialog(): void {
    const resource = this.resource();
    const opener = this._moveOpener;
    if (!resource?.id || !resource?.resourceName || !opener) {
      return;
    }
    opener({
      shareableType: resource.resourceName,
      shareableId: resource.id,
      shareableName: resource.name,
      shareableTypeLabel: SHAREABLE_TYPE_DISPLAY_NAME[resource.resourceName],
      modifiedDate: resource.modifiedDate?.toISOString(),
    })
      .pipe(take(1))
      .subscribe((result) => {
        if (result) {
          this.moved.emit(result.targetFolderId);
        }
      });
  }

  private _openPermissionsDialog(): void {
    const resource = this.resource();
    const opener = this._permissionsOpener;
    if (!resource?.id || !resource?.resourceName || !opener) {
      return;
    }
    opener({
      shareableType: resource.resourceName,
      shareableId: resource.id,
      shareableName: resource.name,
    })
      .pipe(take(1))
      .subscribe();
  }

  private _openEditLabelsDialog(): void {
    const resource = this.resource();
    const opener = this._editLabelsOpener;
    if (!resource?.id || !resource?.resourceName || !opener) {
      return;
    }
    opener({
      shareableType: resource.resourceName,
      shareableId: resource.id,
      shareableName: resource.name,
    })
      .pipe(take(1))
      .subscribe((labels: ShareableLabel[] | null) => {
        if (labels) {
          // Drop a still-pending first load so it cannot overwrite the edit.
          this._resetFetchedLabels();
          this._fetchedLabels.set(labels);
          this.labelsChange.emit(labels);
        }
      });
  }

  private _confirmDelete(): void {
    const resource = this.resource();
    const shareableTypeDisplayName =
      SHAREABLE_TYPE_DISPLAY_NAME[resource.resourceName];
    const opener = this._archiveOpener;
    if (opener) {
      opener({
        shareableType: resource.resourceName,
        shareableId: resource.id,
        shareableName: resource.name,
      })
        .pipe(take(1))
        .subscribe((confirmed) => {
          if (confirmed) {
            this.deleteResource.emit();
          }
        });
      return;
    }
    this._confirmService.confirm({
      header: `${shareableTypeDisplayName} löschen ?`,
      message: `Bitte Bestätigen, ob ${shareableTypeDisplayName} gelöscht werden soll`,
      icon: 'pi pi-trash',
      acceptLabel: 'Ja, löschen',
      acceptButtonStyleClass: 'p-button-danger',
      rejectLabel: 'Abbrechen',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: () => {
        this.deleteResource.emit();
      },
    });
  }
}
